import { computed, ref, watch } from 'vue'
import {
  listManageFeedbackTickets,
  listMyFeedbackTickets,
  type FeedbackTicketListItem,
} from '@/common/apis/feedback'
import { useUserStore } from '@/pinia/stores/user'
import { usePermissionStore } from '@/pinia/stores/permission'
import { hasFeedbackManageMenu } from '@/common/utils/menu-route'
import { openFeedbackDrawer } from './useFeedbackDrawer'
import { onFeedbackRealtime } from './useFeedbackRealtime'

/** 有未读的工单数（不是消息条数累加） */
function countUnreadTickets(
  list: FeedbackTicketListItem[],
  key: 'userUnreadCount' | 'staffUnreadCount',
) {
  return list.reduce((sum, item) => sum + (Number(item[key]) > 0 ? 1 : 0), 0)
}

const userUnreadTicketCount = ref(0)
const staffUnreadTicketCount = ref(0)
let inited = false
let userRefreshSeq = 0
let staffRefreshSeq = 0

export function applyFeedbackUnreadFromList(list: FeedbackTicketListItem[]) {
  userUnreadTicketCount.value = countUnreadTickets(list, 'userUnreadCount')
}

export function applyFeedbackStaffUnreadFromList(list: FeedbackTicketListItem[]) {
  staffUnreadTicketCount.value = countUnreadTickets(list, 'staffUnreadCount')
}

export async function refreshFeedbackUnread() {
  const seq = ++userRefreshSeq
  const userStore = useUserStore()
  if (!userStore.token) {
    if (seq === userRefreshSeq) userUnreadTicketCount.value = 0
    return
  }
  try {
    const res = await listMyFeedbackTickets({ pageNo: 1, pageSize: 50 })
    if (seq !== userRefreshSeq) return
    applyFeedbackUnreadFromList(res.data?.data?.list || [])
  } catch {
    // 保留上次数量，避免入口角标闪烁消失
  }
}

export async function refreshFeedbackStaffUnread() {
  const seq = ++staffRefreshSeq
  const userStore = useUserStore()
  if (!userStore.token || !hasFeedbackManageMenu()) {
    if (seq === staffRefreshSeq) staffUnreadTicketCount.value = 0
    return
  }
  try {
    const res = await listManageFeedbackTickets({ pageNo: 1, pageSize: 100 })
    if (seq !== staffRefreshSeq) return
    applyFeedbackStaffUnreadFromList(res.data?.data?.list || [])
  } catch {
    // 无管理权限或请求失败时不清成 0 以外的闪烁：无菜单则上面已置 0
  }
}

/** 顶栏 / 任务大厅悬浮按钮：有未读进列表，否则进提交表单 */
export function openFeedbackEntry() {
  ensureInit()
  openFeedbackDrawer({ view: userUnreadTicketCount.value > 0 ? 'list' : 'form' })
}

function ensureInit() {
  if (inited) return
  inited = true
  const userStore = useUserStore()
  const permissionStore = usePermissionStore()

  watch(
    () => userStore.token,
    (token) => {
      if (token) {
        void refreshFeedbackUnread()
        void refreshFeedbackStaffUnread()
      } else {
        userUnreadTicketCount.value = 0
        staffUnreadTicketCount.value = 0
      }
    },
    { immediate: true },
  )

  watch(
    () => permissionStore.menuTree,
    () => {
      if (userStore.token) void refreshFeedbackStaffUnread()
    },
  )

  onFeedbackRealtime((payload) => {
    // 用户自己发出的回复 / 新工单创建：只影响管理员侧未读
    const staffOnly =
      payload.event === 'FEEDBACK_CREATED' ||
      (payload.event === 'FEEDBACK_REPLY' && payload.senderType === 'USER')

    if (!staffOnly) {
      void refreshFeedbackUnread()
    }
    void refreshFeedbackStaffUnread()
  })
}

export function useFeedbackUnread() {
  ensureInit()
  return {
    unreadCount: computed(() => userUnreadTicketCount.value),
    refresh: refreshFeedbackUnread,
    applyFromList: applyFeedbackUnreadFromList,
  }
}

/** 管理员未读工单数（顶栏「帮助与反馈」图标角标会合并展示） */
export function useFeedbackStaffUnread() {
  ensureInit()
  return {
    unreadCount: computed(() => staffUnreadTicketCount.value),
    refresh: refreshFeedbackStaffUnread,
    applyFromList: applyFeedbackStaffUnreadFromList,
  }
}
